import {EventDataType} from '@gongxigames/framework/core';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {FlowModule} from '@gongxigames/framework/slot/modules/flow/FlowModule';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow/FlowModuleEvents';
import {UIDisableReason} from '@gongxigames/framework/slot/modules/keypad';
import {WaitingModule} from '@gongxigames/framework/slot/modules/waiting/WaitingModule';
import {ModuleState} from '@gongxigames/framework/slot/fragments/module/ModuleState';

/**
 * [event to wait for] : event to fire
 */
export type WaitingEventsQueue = Record<number|string, symbol|string>;

export class FlowState extends ModuleState<null, FlowModule, GlobalEventsMapForBackwardCompatibility> {

    protected _eventsQueue: WaitingEventsQueue = {};

    public onEnterState(): void {
    }

    public onLeaveState(): void {
        this.clearEventsQueue();
    }

    public handle<K extends keyof GlobalEventsMapForBackwardCompatibility>(
        id: K,
        ...args: EventDataType<GlobalEventsMapForBackwardCompatibility[K]>
    ): void {
        super.handle(id, ...args);

        this.handleWaitingList(id as string);
    }

    protected setupEvents(): void {
    }

    protected setupNoWin(): void {
        this.emit(FlowModuleEvents.NO_WIN);
        this.emit(FlowModuleEvents.CAN_SPIN);
    }

    protected handleWaitingList(id: string, data?: any): void {
        if (this.eventsQueue[id]) {
            this.reactToEventFromWaitingList(id, this.eventsQueue[id], data);
        }
    }

    protected addEventToWaitingList(waitingForEvent: string | symbol, eventToFire: string | symbol, ...args: any[]): void {
        this.eventsQueue[waitingForEvent as string] = eventToFire;
        this.module.eventManager.on(waitingForEvent, this.reactToEventFromWaitingList.bind(this, waitingForEvent, eventToFire,
            ...args), this);
    }

    protected addEventToWaitingListWithPriority(waitingForEvent: string | symbol, eventToFire: string | symbol, priority: number, ...args: any[]): void {
        this.eventsQueue[waitingForEvent as string] = eventToFire;
        this.module.eventManager.on(waitingForEvent, this.reactToEventFromWaitingList.bind(this, waitingForEvent, eventToFire, ...args),
            this, priority);
    }

    protected addEventToWaitingListWithCallback(waitingForEvent: string | symbol, eventToFire: string | symbol, callback: Function,
        ...args: any[]): void {
        this.eventsQueue[waitingForEvent as string] = eventToFire;
        this.module.eventManager.on(waitingForEvent,
            this.reactToEventFromWaitingListWithCallback.bind(this, waitingForEvent, eventToFire, callback, args), this);
    }

    protected addCallbackToWaitingLIst(waitingForEvent: string | symbol, callback: Function, ...args: any[]): void {
        this.addEventToWaitingListWithCallback(waitingForEvent, '', callback, ...args);
    }

    protected reactToEventFromWaitingList(waitingForEvent: string | symbol, eventToFire: string | symbol, ...args: any[]): void {
        eventToFire && this.emit(eventToFire, ...args);
        this.module.eventManager.off(waitingForEvent, this);
    }

    protected reactToEventFromWaitingListWithCallback(waitingForEvent: string | symbol, eventToFire: string | symbol, callback: Function,
        ...args: any[]): void {
        if (this._eventsQueue.hasOwnProperty(waitingForEvent)) {
            callback.call(this);
            eventToFire && this.emit(eventToFire, args);
            this.module.eventManager.off(waitingForEvent, this);
        }
    }

    protected clearEventsQueue(): void {
        // for string keys
        Object.getOwnPropertyNames(this.eventsQueue).forEach(event => {
            this.module.eventManager.off(event, this);
        });

        // for symbol keys
        Object.getOwnPropertySymbols(this.eventsQueue).forEach(event => {
            this.module.eventManager.off(event, this);
        });

        this._eventsQueue = {};
    }

    protected removeEventsFromEventsQueue(removeEvents: (string | symbol)[]): void {
        removeEvents.forEach(event => {
            this.module.eventManager.off(event, this);
            delete this._eventsQueue[event as string];
        });
    }

    protected removeEventsFromEventsQueueAfterEvent(afterEvent: symbol | string, removeEvents: (string | symbol)[]): void {
        this.addCallbackToWaitingLIst(afterEvent, () => {
            this.removeEventsFromEventsQueue(removeEvents);
        });
    }

    protected addToWaitingForResources(callback: Function): void {
        this.emit(WaitingModule.SHOW_POPUP);
        this.emit('General.UI.DISABLE', UIDisableReason.WAITING);
        this.addEventToWaitingList('General.ResourcesLoading.LOADED_ALL_RESOURCES', 'General.UI.ENABLE', UIDisableReason.WAITING);
        this.addCallbackToWaitingLIst('General.ResourcesLoading.LOADED_ALL_RESOURCES', callback.bind(this));
    }

    protected emitRoundEnded(): void {
        this.emit(FlowModuleEvents.ROUND_ENDED, {
            win: this.module.logic.getWin(),
            totalWin: this.module.logic.getTotalWin(),
            balanceAfter: this.module.logic.getBalance(),
            isBigWin: this.isBigWin()
        });
    }

    protected isWin(): boolean {
        return this.module.logic.getWin() > 0;
    }

    protected isBigWin(): boolean {
        return this.module.logic.getWin() >= this.module.logic.getBigWinLimit();
    }

    get eventsQueue(): WaitingEventsQueue {
        return this._eventsQueue;
    }
}
